import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  useMediaQuery, 
  useTheme, 
  Tabs, 
  Tab,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import AdminUsersPage from './AdminUsersPage';
import AdminCardsPage from './AdminCardsPage';
import AdminLoansPage from './AdminLoansPage';
import AdminTransfersPage from './AdminTransfersPage';
import AdminKycPage from './AdminKycPage';
import { useNavigate } from 'react-router-dom';
import { getUserFromToken } from '../utils/auth';
import HeaderBar from '../components/HeaderBar';
import AccountStatusControl from '../components/AccountStatusControl';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SettingsIcon from '@mui/icons-material/Settings';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const navItems = [
  { label: 'Users', icon: <PeopleIcon /> },
  { label: 'Cards', icon: <CreditCardIcon /> },
  { label: 'Loans', icon: <AccountBalanceWalletIcon /> },
  { label: 'Transfers', icon: <SwapHorizIcon /> },
  { label: 'KYC Verification', icon: <VerifiedUserIcon /> },
  { label: 'Account Status', icon: <SettingsIcon /> }
];

export default function AdminDashboardPage() {
  const [selected, setSelected] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const user = getUserFromToken();
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [navigate]);

  // Content based on selected tab
  let content;
  if (selected === 0) content = <AdminUsersPage />;
  else if (selected === 1) content = <AdminCardsPage />;
  else if (selected === 2) content = <AdminLoansPage />;
  else if (selected === 3) content = <AdminTransfersPage />;
  else if (selected === 4) content = <Box p={3}><Typography variant="h5" mb={3}>KYC Verification</Typography><AdminKycPage /></Box>;
  else if (selected === 5) content = <Box p={3}><Typography variant="h5" mb={3}>Account Status</Typography><AccountStatusControl /></Box>;
  else content = <Box p={3}><Typography variant="h5">Coming soon...</Typography></Box>;

  // Mobile drawer for navigation
  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
        <Box component="img" src="/logo.jpg" alt="Safenet Bank Logo" sx={{ width: 60, height: 60, borderRadius: '4px' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3b1e6d' }}>
          Admin Panel
        </Typography>
      </Box>
      <Divider />
      <List>
        {navItems.map((item, idx) => (
          <ListItem 
            button 
            key={item.label} 
            onClick={() => {
              setSelected(idx);
              setDrawerOpen(false);
            }}
            sx={{
              bgcolor: selected === idx ? 'rgba(59, 30, 109, 0.1)' : 'transparent',
              color: selected === idx ? '#3b1e6d' : 'inherit',
              fontWeight: selected === idx ? 600 : 400,
            }}
          >
            <ListItemIcon sx={{ color: selected === idx ? '#3b1e6d' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box>
      <HeaderBar />
      
      {/* Mobile view */}
      {isMobile && (
        <>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
            <IconButton 
              color="inherit" 
              aria-label="open drawer"
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {navItems[selected].label}
            </Typography>
          </Box>
          
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
          >
            {drawer}
          </Drawer>
        </>
      )}
      
      {/* Desktop view */}
      {!isMobile && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Tabs 
            value={selected} 
            onChange={(e, newValue) => setSelected(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minWidth: 120,
                fontWeight: 600,
              },
              '& .Mui-selected': {
                color: '#3b1e6d !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#3b1e6d',
              },
            }}
          >
            {navItems.map((item, idx) => (
              <Tab 
                key={item.label} 
                label={item.label} 
                icon={item.icon} 
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>
      )}
      
      <Box flex={1} p={{ xs: 2, md: 4 }}>
        {content}
      </Box>
    </Box>
  );
}
